import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { memo } from 'react';
import { Accordion } from 'src/components/Accordion';
import { Box } from 'src/components/Box';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { PlatformRole } from 'src/models/platform/_types';
import TrainerPermissions from 'src/pages/Staff/NewStaff/TrainerPermissions';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { Identifier } from 'src/types/utilityTypes';

interface RoleEditorProps {
  onDelete: () => Promise<void>;
  canEdit: boolean;
  index: number;
  setRoleRef: (id: string, el: HTMLElement | null) => void;
  role: PlatformRole;
  expanded: boolean;
  onToggle: (id: Identifier) => void;
}

// Memoized component to prevent unnecessary re-renders
export const RoleEditor = memo(
  ({
    onDelete,
    canEdit,
    index,
    setRoleRef,
    role,
    expanded,
    onToggle,
  }: RoleEditorProps) => {
    const roleFieldName = `defaultChildPlatformRoles.${index}`;
    const nameFieldName = `${roleFieldName}.name`;
    const permissionsFieldName = `${roleFieldName}.permissions`;

    const [confirmDialogProps, confirmDialog] = useConfirmDialogComponent({
      title: 'Delete Role',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmColor: 'error',
    });

    return (
      <Box
        key={role.id}
        ref={(el: HTMLElement | null) => {
          if (role.id) {
            setRoleRef(role.id.toString(), el);
          }
        }}
      >
        <Accordion
          expanded={expanded}
          onChange={() => onToggle(role.id!)}
          label={
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ pr: 3 }}
            >
              <Typography variant="inherit">{role.name}</Typography>

              {canEdit && (
                <IconButton
                  color="error"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const confirmed = await confirmDialog(
                      `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`
                    );
                    if (confirmed) {
                      await onDelete();
                    }
                  }}
                >
                  <Icon icon={icon({ name: 'trash', style: 'regular' })} />
                </IconButton>
              )}
            </Stack>
          }
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              <FormTextField name={nameFieldName} fast disabled={!canEdit} />
            </Stack>
            <TrainerPermissions namePrefix={permissionsFieldName} />
          </Stack>
        </Accordion>
        <ConfirmDialog {...confirmDialogProps} />
      </Box>
    );
  }
);

RoleEditor.displayName = 'RoleEditor';
